import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

function findChildToFocus(container) {
  return container.querySelector('[aria-selected="true"]') ?? container.firstChild;
}

/**
 * FocusableList
 * @param {import('.').FocusableListProps}
 */
export default function FocusableList({ children, className, onEscapePressed, ...aria }) {
  const ref = useRef();
  const lastFocus = useRef();

  useEffect(() => {
    const container = ref.current;
    if (!container) {
      return undefined;
    }

    // Attempt to grab the currently focused element, if we can, while respecting
    // shadow root nodes (if supported).
    /* eslint-disable-next-line no-restricted-globals */
    const root = container.getRootNode ? container.getRootNode() : document;
    if (root) {
      /* eslint-disable-next-line no-restricted-globals */
      lastFocus.current = root.activeElement ?? document.activeElement;
    }

    // Move focus in to our list, being the currently selected item (if available),
    // otherwise focus the first child in the list.
    const child = findChildToFocus(container);
    if (child) {
      setTimeout(() => child.focus(), 0);
    }

    // If we were provided an escape handler and we have a root node, add a listener
    // to the root node for a click outside and call the escape handler.
    if (onEscapePressed && root) {
      const clickListener = (ev) => {
        // If the click was not contained in our list, call the handler to close and
        // stop all propigation and prevent default if we're told to.
        if (!container.contains(ev.target) && onEscapePressed()) {
          ev.preventDefault();
          ev.stopPropagation();
        }
      };

      root.addEventListener('click', clickListener, true);
      return () => {
        root.removeEventListener('click', clickListener, true);
      };
    }

    return undefined;
  }, [onEscapePressed]);

  const keyDown = useCallback(
    (ev) => {
      const container = ref.current;
      if (!container) {
        return;
      }

      switch (ev.key) {
        case 'Tab':
          if (!ev.shiftKey && ev.target === container.lastChild) {
            container.firstChild.focus();
          } else if (ev.shiftKey && ev.target === container.firstChild) {
            container.lastChild.focus();
          } else {
            // Allow the default browser action to move focus normally.
            return;
          }
          break;
        case 'ArrowUp':
          if (ev.target !== container.firstChild) {
            ev.target.previousSibling.focus();
          }
          break;
        case 'ArrowDown':
          if (ev.target !== container.lastChild) {
            ev.target.nextSibling.focus();
          }
          break;
        case 'Home':
          container.firstChild.focus();
          break;
        case 'End':
          container.lastChild.focus();
          break;
        case ' ':
          // Prevent default in the case of space.
          break;
        case 'Escape':
          if (onEscapePressed && onEscapePressed()) {
            // The escape was handled, so give focus back to the original element,
            // if it's available.
            const focusTarget = lastFocus.current;
            if (focusTarget) {
              setTimeout(() => focusTarget.focus(), 0);
            }

            return;
          }
          break;
        default:
          // No recognized key, so return to allow default handling to continue.
          return;
      }

      ev.preventDefault();
    },
    [onEscapePressed],
  );

  return (
    <div
      aria-multiselectable="true"
      className={className}
      onFocus={(ev) => {
        // If we get focus directly, move focus to the correct location inside our children.
        if (ev.target === ref.current) {
          const child = findChildToFocus(ref.current);
          if (child) {
            child.focus();
          }
        }
      }}
      onKeyDown={keyDown}
      ref={ref}
      role="listbox"
      tabIndex={0}
      {...aria}
    >
      {children}
    </div>
  );
}

FocusableList.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  className: PropTypes.string,
  onEscapePressed: PropTypes.func,
};

FocusableList.defaultProps = {
  className: null,
  onEscapePressed: null,
};
